import React, { useState, useEffect } from 'react';

import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import PersonalInfoSection from './components/PersonalInfoSection';
import SecuritySection from './components/SecuritySection';
import NotificationSection from './components/NotificationSection';
import PrivacySection from './components/PrivacySection';
import TravelPreferencesSection from './components/TravelPreferencesSection';

import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorMessage from '../../components/ui/ErrorMessage';
import { newsletterService } from '../../services/newsletterService';


const UserProfileManagement = () => {
  const { user, signOut } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('personal');
  const [successMessage, setSuccessMessage] = useState('');
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorEnabled: false
  });
  const [notificationSettings, setNotificationSettings] = useState({});
  const [privacySettings, setPrivacySettings] = useState({});
  const [travelPreferences, setTravelPreferences] = useState({});

  const fetchUserProfile = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();

      if (fetchError) throw fetchError;

      // Transform database fields to component format
      const transformedProfile = {
        firstName: data?.full_name?.split(' ')?.[0] || '',
        lastName: data?.full_name?.split(' ')?.slice(1)?.join(' ') || '',
        email: data?.email || '',
        phone: data?.phone || '',
        location: data?.preferences?.location || '',
        dateOfBirth: data?.preferences?.dateOfBirth || '',
        travelStyle: data?.preferences?.travelStyle || '',
        avatarUrl: data?.avatar_url || '',
        role: data?.role || 'user',
        isActive: data?.is_active || true,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };

      setUserProfile(transformedProfile);
      
      // Load travel preferences from database
      const travelPrefs = data?.preferences || {};
      setTravelPreferences({
        preferredParkTypes: travelPrefs?.preferredParkTypes || [],
        activityInterests: travelPrefs?.activityInterests || [],
        accommodationStyle: travelPrefs?.accommodationStyle || '',
        groupSize: travelPrefs?.groupSize || '',
        budgetRange: travelPrefs?.budgetRange || '',
        travelSeason: travelPrefs?.travelSeason || [],
        difficultyLevel: travelPrefs?.difficultyLevel || '',
        accessibilityNeeds: travelPrefs?.accessibilityNeeds || []
      });

      // ✅ UPDATED: Load privacy settings from dedicated column
      setPrivacySettings({
        profileVisibility: travelPrefs?.profileVisibility || 'private',
        tripSharing: travelPrefs?.tripSharing || 'private',
        defaultTripPrivacy: data?.default_trip_visibility || 'private'
      });

      // Load notification settings from database
      setNotificationSettings({
        emailNotifications: travelPrefs?.emailNotifications || {
          newsletter: false
        }
      });
    } catch (err) {
      console.error('Error fetching user profile:', err);
      setError(err?.message || 'Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchUserProfile();
    }
  }, [user?.id]);

  const handleUpdateProfile = async (updatedData) => {
    try {
      setError(null);
      setSuccessMessage('');

      // Get current preferences to preserve all settings
      const { data: currentProfile, error: fetchError } = await supabase?.from('user_profiles')?.select('preferences')?.eq('id', user?.id)?.single();

      if (fetchError) throw fetchError;

      // Transform component format to database format
      const fullName = `${updatedData?.firstName || ''} ${updatedData?.lastName || ''}`?.trim();
      
      // Merge personal info with existing preferences
      const updatedPreferences = {
        ...(currentProfile?.preferences || {}),
        location: updatedData?.location || '',
        dateOfBirth: updatedData?.dateOfBirth || '',
        travelStyle: updatedData?.travelStyle || ''
      };

      const { error: updateError } = await supabase?.from('user_profiles')?.update({
          full_name: fullName,
          email: updatedData?.email,
          phone: updatedData?.phone || '',
          preferences: updatedPreferences,
          updated_at: new Date()?.toISOString()
        })?.eq('id', user?.id);

      if (updateError) throw updateError;

      setSuccessMessage('Profile updated successfully!');
      
      // Reload profile data to reflect changes
      await fetchUserProfile();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err?.message || 'Failed to update profile');
      throw err;
    }
  };

  const handleUpdateSecurity = async (securityData) => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));

    if (securityData?.type === 'password') {
      // Handle password change
      console.log('Password updated successfully');
    } else if (securityData?.type === 'twoFactor') {
      setSecuritySettings((prev) => ({
        ...prev,
        twoFactorEnabled: securityData?.enabled
      }));
    } else if (securityData?.type === 'terminateSession') {
      console.log('Session terminated:', securityData?.sessionId);
    }
  };

  const handleUpdateNotifications = async (notificationData) => {
    try {
      setError(null);
      setSuccessMessage('');

      // Get current preferences
      const { data: currentProfile, error: fetchError } = await supabase?.from('user_profiles')?.select('preferences, email')?.eq('id', user?.id)?.single();

      if (fetchError) throw fetchError;

      // Merge notification settings with existing preferences
      const updatedPreferences = {
        ...(currentProfile?.preferences || {}),
        emailNotifications: notificationData?.emailNotifications
      };

      // Update in database
      const { error: updateError } = await supabase?.from('user_profiles')?.update({
          preferences: updatedPreferences,
          updated_at: new Date()?.toISOString()
        })?.eq('id', user?.id);

      if (updateError) throw updateError;

      // Handle newsletter subscription in newsletter_subscriptions table
      const newsletterOptIn = notificationData?.emailNotifications?.newsletter;
      const userEmail = currentProfile?.email;

      if (newsletterOptIn) {
        // Subscribe user to newsletter
        await newsletterService?.subscribeUser(userEmail);
      } else {
        // Unsubscribe user from newsletter
        await newsletterService?.unsubscribeUser(userEmail);
      }

      // Reload profile to get fresh data
      await fetchUserProfile();
      
      setSuccessMessage('Notification settings updated successfully!');
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating notification settings:', err);
      setError(err?.message || 'Failed to update notification settings');
      throw err;
    }
  };

  const handleUpdatePrivacy = async (privacyData) => {
    try {
      setError(null);
      setSuccessMessage('');

      // Get current preferences
      const { data: currentProfile, error: fetchError } = await supabase?.from('user_profiles')?.select('preferences, default_trip_visibility')?.eq('id', user?.id)?.single();

      if (fetchError) throw fetchError;

      // ✅ NEW: Update dedicated column for default trip visibility
      const updateData = {
        default_trip_visibility: privacyData?.defaultTripPrivacy,
        updated_at: new Date()?.toISOString()
      };

      // Update in database
      const { error: updateError } = await supabase?.from('user_profiles')?.update(updateData)?.eq('id', user?.id);

      if (updateError) throw updateError;

      // Reload profile to get fresh data
      await fetchUserProfile();
      
      setSuccessMessage('Privacy settings updated successfully!');
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating privacy settings:', err);
      setError(err?.message || 'Failed to update privacy settings');
      throw err;
    }
  };

  const handleUpdatePreferences = async (preferencesData) => {
    try {
      setError(null);
      setSuccessMessage('');

      // Get current preferences
      const { data: currentProfile, error: fetchError } = await supabase?.from('user_profiles')?.select('preferences')?.eq('id', user?.id)?.single();

      if (fetchError) throw fetchError;

      // Merge travel preferences with existing preferences
      const updatedPreferences = {
        ...(currentProfile?.preferences || {}),
        preferredParkTypes: preferencesData?.preferredParkTypes,
        activityInterests: preferencesData?.activityInterests,
        accommodationStyle: preferencesData?.accommodationStyle,
        groupSize: preferencesData?.groupSize,
        budgetRange: preferencesData?.budgetRange,
        travelSeason: preferencesData?.travelSeason,
        difficultyLevel: preferencesData?.difficultyLevel,
        accessibilityNeeds: preferencesData?.accessibilityNeeds
      };

      // Save to database
      const { error: updateError } = await supabase?.from('user_profiles')?.update({
          preferences: updatedPreferences,
          updated_at: new Date()?.toISOString()
        })?.eq('id', user?.id);

      if (updateError) throw updateError;

      // Reload profile to get fresh data
      await fetchUserProfile();
      
      setSuccessMessage('Travel preferences updated successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating travel preferences:', err);
      setError(err?.message || 'Failed to update travel preferences');
      throw err;
    }
  };

  const handleDeleteAccount = async () => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 2000));
    console.log('Account deleted successfully');
    // Redirect to home page or login
  };

  const handleLogout = () => {
    signOut();
  };

  const tabs = [
    { id: 'personal', label: 'Personal Info', icon: 'User' },
    { id: 'preferences', label: 'Travel Preferences', icon: 'Map' },
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
    { id: 'privacy', label: 'Privacy', icon: 'Lock' },
    { id: 'security', label: 'Security', icon: 'Shield' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'personal':
        return (
          <PersonalInfoSection 
            userProfile={userProfile}
            onUpdateProfile={handleUpdateProfile}
          />
        );
      case 'preferences':
        return (
          <TravelPreferencesSection
            travelPreferences={travelPreferences}
            onUpdatePreferences={handleUpdatePreferences}
          />
        );
      case 'notifications':
        return (
          <NotificationSection
            settings={notificationSettings}
            onUpdateNotifications={handleUpdateNotifications}
          />
        );
      case 'privacy':
        return (
          <PrivacySection
            privacySettings={privacySettings}
            onUpdatePrivacy={handleUpdatePrivacy}
            onDeleteAccount={handleDeleteAccount}
          />
        );
      case 'security':
        return (
          <SecuritySection
            settings={securitySettings}
            onUpdateSecurity={handleUpdateSecurity}
            onDeleteAccount={handleDeleteAccount}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header isAuthenticated={!!user} user={user || null} onLogout={handleLogout} />
        <main className="pt-16">
          <div className="container mx-auto px-4 py-8">
            <LoadingSpinner size="lg" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header isAuthenticated={!!user} user={userProfile || user || null} onLogout={handleLogout} />
      <main className="pt-16">
        <div className="container mx-auto px-4 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="font-heading font-heading-bold text-3xl text-foreground mb-2">
              Profile Settings
            </h1>
            <p className="font-body text-muted-foreground">
              Manage your account, preferences, and privacy settings
            </p>
          </div>

          {/* Success/Error Messages */}
          {successMessage && (
            <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-organic text-success">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} />
                <span>{successMessage}</span>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6">
              <ErrorMessage error={error} />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar Navigation */}
            <div className="lg:col-span-1">
              <div className="bg-card rounded-organic border border-border p-4 sticky top-24">
                <nav className="space-y-2">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-organic-sm text-left transition-organic ${
                        activeTab === tab?.id
                          ? 'bg-primary text-primary-foreground'
                          : 'text-card-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon
                        name={tab?.icon}
                        size={20}
                        color={activeTab === tab?.id ? 'white' : 'currentColor'}
                      />
                      <span className="font-body font-body-medium text-sm">{tab?.label}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserProfileManagement;